/*
 * Copyright (c) 2020 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import { createTheme, alpha } from "@material-ui/core/styles";

const theme = createTheme({
    palette: {
        background: {
            default: "#ffffff",
        },
        divider: "#dbdde1",
        error: {
            dark: "#dd494f",
            main: "#ff8400",
        },
        primary: {
            light: "#f4fcfe",
            main: "#20b8e9",
        },
        secondary: {
            dark: "#849297",
            light: "#e7f0f2",
            main: "#1b363f",
        },
        success: {
            dark: "#0E734F",
            light: "#C0EBDC",
            main: "#50aa73",
        },
        text: {
            hint: "#6a8796",
            primary: "#1b363f",
            secondary: "#79898e",
        },
    },
    props: {
        MuiButtonBase: {
            disableRipple: true,
        },
    },
    typography: {
        body1: {
            fontSize: 14,
        },
        body2: {
            fontSize: 11,
        },
        fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
        fontWeightMedium: 600,
        h1: {
            fontSize: 21,
            letterSpacing: -1,
        },
        h2: {
            fontSize: 19,
        },
        h3: {
            fontSize: 18,
        },
        h4: {
            fontSize: 17,
        },
        h5: {
            fontSize: 16,
        },
        h6: {
            fontSize: 15,
        },
    },
});
theme.overrides = {
    MuiAppBar: {
        colorPrimary: {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.text.primary,
        },
    },
    MuiBackdrop: {
        root: {
            backgroundColor: alpha(theme.palette.common.white, 0.5),
        },
    },
    MuiButton: {
        contained: {
            "&$disabled": {
                backgroundColor: "#acbdc6",
                boxShadow: "none",
                color: theme.palette.common.white,
            },
            "&$focusVisible": {
                boxShadow: "none",
            },
            "&:active": {
                boxShadow: "none",
            },
            "&:hover": {
                "&$disabled": {
                    backgroundColor: "#acbdc6",
                },
                "@media (hover: none)": {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: "none",
                },
                backgroundColor: theme.palette.primary.dark,
                boxShadow: "none",
            },
            backgroundColor: theme.palette.primary.main,
            borderRadius: theme.spacing(8),
            boxShadow: "none",
            color: theme.palette.common.white,
            lineHeight: 1.8,
            paddingBottom: theme.spacing(1),
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            paddingTop: theme.spacing(1),
        },
        root: {
            fontSize: theme.typography.h4.fontSize,
            fontWeight: theme.typography.fontWeightBold as any,
            lineHeight: 1.2,
            textAlign: "center",
            textTransform: "none",
        },
        textSecondary: {
            color: theme.palette.text.secondary,
        },
    },
    MuiContainer: {
        root: {
            [theme.breakpoints.up("sm")]: {
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
            },
            marginBottom: theme.spacing(2),
            marginTop: theme.spacing(2),
            maxWidth: theme.breakpoints.values.xl,
        },
    },
    MuiDialog: {
        paper: {
            [theme.breakpoints.up("lg")]: {
                marginTop: theme.spacing(12),
            },
            marginTop: theme.spacing(8),
        },
        paperScrollPaper: {
            [theme.breakpoints.up("lg")]: {
                maxHeight: "calc(100% - 160px)",
            },
            maxHeight: "calc(100% - 96px)",
        },
        paperWidthXs: {
            "&$paperScrollBody": {
                [theme.breakpoints.down(Math.max(theme.breakpoints.values.xs, 400) + 32 * 2)]: {
                    maxWidth: "calc(100% - 64px)",
                },
            },
            maxWidth: Math.max(theme.breakpoints.values.xs, 400),
        },
        root: {
            zIndex: theme.zIndex.snackbar + 1,
        },
        scrollPaper: {
            alignItems: "flex-start",
        },
    },
    MuiDialogActions: {
        root: {
            "& .MuiButton-text": {
                paddingBottom: theme.spacing(1),
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1),
                paddingTop: theme.spacing(1),
            },
            paddingBottom: theme.spacing(1),
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            paddingTop: theme.spacing(1),
        },
    },
    MuiDrawer: {
        paper: {
            width: theme.spacing(32),
        },
    },
    MuiFab: {
        root: {
            "&$disabled": {
                boxShadow: "none",
            },
            "&$focusVisible": {
                boxShadow: "none",
            },
            "&:active": {
                boxShadow: "none",
            },
            "&:hover": {
                boxShadow: "none",
            },
            boxShadow: "none",
            textTransform: "none",
        },
    },
    MuiIconButton: {
        colorPrimary: {
            "&:hover": {
                backgroundColor: "transparent",
                color: theme.palette.text.secondary,
            },
            color: theme.palette.text.primary,
            padding: 0,
        },
        root: {
            "&:hover": {
                backgroundColor: "transparent",
            },
            padding: 0,
        },
    },
    MuiListItem: {
        button: {
            "&:hover": {
                "@media (hover: none)": {
                    backgroundColor: "transparent",
                },
                backgroundColor: "transparent",
            },
        },
        root: {
            fontSize: theme.typography.h3.fontSize,
        },
    },
    MuiMenuItem: {
        root: {
            fontSize: theme.typography.h3.fontSize,
        },
    },
    MuiOutlinedInput: {
        notchedOutline: {
            borderColor: theme.palette.secondary.light,
            borderWidth: theme.spacing(0.25),
        },
        root: {
            "&$disabled $notchedOutline": {
                borderColor: theme.palette.secondary.light,
            },
            "&$error $notchedOutline": {
                borderColor: theme.palette.error.main,
            },
            "&$focused $notchedOutline": {
                borderColor: theme.palette.primary.main,
            },
            "&:hover $notchedOutline": {
                borderColor: "transparent",
            },
            // Reset on touch devices, it doesn't add specificity
            "@media (hover: none)": {
                "&:hover $notchedOutline": {
                    borderColor: theme.palette.secondary.light,
                },
            },
            backgroundColor: theme.palette.secondary.light,
            borderRadius: theme.shape.borderRadius,
            borderWidth: theme.spacing(0.25),
        },
    },
    MuiPaper: {
        rounded: {
            borderRadius: theme.spacing(1),
        },
    },
    MuiToolbar: {
        gutters: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            [theme.breakpoints.up("sm")]: {
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
            },
        },
    },
    MuiTypography: {
        h1: {
            fontWeight: theme.typography.fontWeightBold as any,
        },
        h2: {
            fontWeight: theme.typography.fontWeightMedium as any,
        },
        h3: {
            fontWeight: theme.typography.fontWeightBold as any,
        },
        h4: {
            fontWeight: theme.typography.fontWeightMedium as any,
        },
        h5: {
            fontWeight: theme.typography.fontWeightMedium as any,
        },
        h6: {
            fontWeight: theme.typography.fontWeightMedium as any,
        },
    },
};

export default theme;
